import React, { useEffect, useMemo, useState } from "react"
import { getFunctions, httpsCallable } from "firebase/functions"
import { getApp } from "firebase/app"
import { Typography, Space, Spin, Alert, Row, Col, Table, Tag, Divider } from "antd"
import { BankOutlined, ArrowUpOutlined, ArrowDownOutlined } from "@ant-design/icons"
import PageHeader from "../../../components/Elements/PageHeader"
import dayjs from "dayjs"

const { Title, Text } = Typography

// hard-coded profile
const PROFILE_ID = 69484976

export default function BankBilances() {
  const functions = useMemo(() => getFunctions(getApp(), "us-central1"), [])
  const getWiseBalances = useMemo(() => httpsCallable(functions, "getWiseBalances"), [functions])
  const getWiseTransactions = useMemo(
    () => httpsCallable(functions, "getWiseTransactions"),
    [functions],
  )

  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState(null)
  const [balance, setBalance] = useState(null)

  const [transactionsLoading, setTransactionsLoading] = useState(true)
  const [transactionsErr, setTransactionsErr] = useState(null)
  const [transactions, setTransactions] = useState([])

  const loadBalance = async () => {
    setLoading(true)
    setErr(null)
    try {
      const res = await getWiseBalances({ profileId: PROFILE_ID })
      const list = res.data || []
      console.log(list)
      setBalance(list[0] || null)
    } catch (e) {
      setErr(e.message || String(e))
      setBalance(null)
    } finally {
      setLoading(false)
    }
  }

  const loadTransactions = async () => {
    setTransactionsLoading(true)
    setTransactionsErr(null)
    try {
      const res = await getWiseTransactions({ profileId: PROFILE_ID, limit: 10 })
      const txList = res.data || []
      console.log("Transactions:", txList)
      setTransactions(txList)
    } catch (e) {
      setTransactionsErr(e.message || String(e))
      setTransactions([])
    } finally {
      setTransactionsLoading(false)
    }
  }

  useEffect(() => {
    loadBalance()
    loadTransactions()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const columns = [
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      width: "10%",
      align: "center",
      render: (type) => {
        const isIncoming = type === "CREDIT" || type === "credit"
        return (
          <Tag
            color={isIncoming ? "green" : "red"}
            icon={isIncoming ? <ArrowDownOutlined /> : <ArrowUpOutlined />}>
            {isIncoming ? "IN" : "OUT"}
          </Tag>
        )
      },
    },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      width: "15%",
      render: (date) => {
        if (!date) return "-"
        return dayjs(date).format("DD.MM.YYYY HH:mm")
      },
    },
    {
      title: "Description",
      dataIndex: "details",
      key: "details",
      render: (details) => {
        const description = details?.description || "N/A"
        return <Text>{description}</Text>
      },
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      width: "15%",
      align: "right",
      render: (amount) => {
        const value = amount?.value || 0
        const currency = amount?.currency || "EUR"
        const isPositive = value > 0

        return (
          <Text strong style={{ color: isPositive ? "#52c41a" : "#ff4d4f" }}>
            {value > 0 ? "+" : ""}
            {Number(value).toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}{" "}
            {currency}
          </Text>
        )
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: "12%",
      align: "center",
      render: (status) => {
        const statusColors = {
          completed: "success",
          pending: "processing",
          cancelled: "default",
          failed: "error",
        }
        const color = statusColors[status?.toLowerCase()] || "success"
        return <Tag color={color}>{status || "Completed"}</Tag>
      },
    },
  ]

  return (
    <div style={{ padding: 15 }}>
      <Row gutter={[15, 15]}>
        <Col span={24}>
          <PageHeader icon={<BankOutlined />} text="Account Balance (Wise)" />
        </Col>

        {/* Balance Section */}
        <Col span={24}>
          {loading ? (
            <Spin />
          ) : err ? (
            <Alert type="error" message={err} />
          ) : balance ? (
            <Space>
              <Title level={2} style={{ margin: 0 }}>
                {Number(balance.amount?.value || 0).toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </Title>
              <Text type="secondary" style={{ fontSize: 18 }}>
                {balance.amount?.currency}
              </Text>
            </Space>
          ) : (
            <Text type="secondary">No balance found</Text>
          )}
        </Col>

        {/* Transactions Section */}
        <Col span={24}>
          <Divider orientation="left">Recent Transactions</Divider>
          {transactionsLoading ? (
            <Spin tip="Loading transactions..." />
          ) : transactionsErr ? (
            <Alert type="error" message={transactionsErr} />
          ) : (
            <Table
              size="small"
              bordered
              columns={columns}
              dataSource={transactions}
              rowKey={(record) => record.id || record.referenceNumber}
              pagination={false}
              locale={{ emptyText: "No transactions found" }}
            />
          )}
        </Col>
      </Row>
    </div>
  )
}
